import {
  Banner,
  Box,
  Button,
  Grid,
  Input,
  Typography,
  Select,
} from '@oresundsbron/bridge-ui';
import { Bizz, Check, CloseOutlined } from '@oresundsbron/icons';
import { useForm, useFormParams } from '@oresundsbron/use-form';
import { pipe, flow } from 'fp-ts/lib/function';
import { Trans, useTranslation } from 'next-i18next';
import { FC, useCallback, useMemo } from 'react';
import { useCountries } from '../../../hooks/useCountries';
import { RecognitionCard } from './RecognitionCard';
import { ThirdPartySignup, Recognition } from './types';
import { convertErrorKeys, isBizz, isBusinessSkipValidation } from './utils';
import { lookup } from 'fp-ts/lib/Array';
import {
  fromOption,
  chain,
  fromTask,
  match as matchTO,
  filter,
  fromPredicate,
} from 'fp-ts/lib/TaskOption';
import { validateT } from '../../../lib/zod';
import {
  thirdPartyUpdatedBizz,
  ThirdPartyBizz,
} from '@oresundsbron/validators';

type RecognitionsProps = useFormParams<ThirdPartySignup> & {
  disabled: boolean;
  skipValidation?: boolean;
  isBusiness: boolean;
};

const getRecognitionIcon = (
  recognition: Recognition,
  skipValidation?: boolean
) =>
  skipValidation || !recognition.invalid ? (
    <Check className="text-xl text-neutral-400" />
  ) : (
    <CloseOutlined className="text-xl text-red-400" />
  );

const Recognitions: FC<RecognitionsProps> = ({
  namespace,
  defaultValues,
  disabled,
  skipValidation,
  isBusiness,
}) => {
  const { t } = useTranslation([namespace, 'common'], {
    nsMode: 'fallback',
  });

  const countries = useCountries();
  const { registerField, getError, getData, setFieldData, setFieldErrors } =
    useForm<ThirdPartySignup>({
      namespace,
      defaultValues,
    });

  const countryOptions = useMemo(
    () =>
      countries.map(({ key, value }) => ({
        key,
        label: key,
        value,
      })),
    [countries]
  );

  const paymentEntries = useMemo(
    () => getData().payment?.paymentEntries ?? [],
    [getData]
  );

  const onAdd = useCallback(
    (index: number) => {
      const recognition = pipe(paymentEntries, lookup(index), fromOption);

      return pipe(
        recognition,
        chain(
          fromPredicate(
            (r): r is ThirdPartyBizz & { invalid: boolean } =>
              isBizz(r) &&
              isBusinessSkipValidation(isBusiness, r, skipValidation)
          )
        ),
        chain(flow(validateT(thirdPartyUpdatedBizz.spa, t), fromTask)),
        filter((e): e is Record<string, string | string[]> => !!e),
        matchTO(
          () => setFieldData(`payment.paymentEntries.${index}.invalid`, '', ''),
          flow(convertErrorKeys(index), setFieldErrors)
        )
      )();
    },
    [
      paymentEntries,
      t,
      setFieldErrors,
      isBusiness,
      skipValidation,
      setFieldData,
    ]
  );

  return (
    <Box>
      <Typography id="recognitions" as="h3" intent="headline" size="sm" gutter>
        {t('form.sections.recognitions.title')}
      </Typography>
      <Typography
        as="p"
        intent="body"
        size="md"
        className="max-w-[65ch] pb-2 md:pb-4"
      >
        <Trans ns={namespace} i18nKey="form.sections.recognitions.description">
          <Typography as="span" intent="body" size="md" weight="semibold" />
        </Trans>
      </Typography>
      <Box className="flex flex-col">
        {paymentEntries.map((recognition, idx) => (
          <RecognitionCard
            key={idx}
            icon={<Bizz className="text-xl text-primary-600" />}
            statusIcon={getRecognitionIcon(
              recognition,
              !isBusinessSkipValidation(isBusiness, recognition, skipValidation)
            )}
            label={
              isBizz(recognition)
                ? t('form.labels.bizzNumber')
                : t('form.labels.licensePlate')
            }
            value={
              isBizz(recognition)
                ? recognition.bizzName ?? recognition.licensePlate
                : recognition.licensePlate
            }
          >
            {!skipValidation && recognition.invalid ? (
              <Grid className="gap-2 pt-4 md:gap-4">
                {!isBusiness ? (
                  <Banner color="inform" className="col-span-full">
                    {t('form.sections.payment.disclaimers.licensePlate')}
                  </Banner>
                ) : null}
                <Select
                  label={t('form.labels.country')}
                  placeholder={t('form.placeholders.country')}
                  options={countryOptions}
                  {...registerField(`payment.paymentEntries.${idx}.country`)}
                  error={getError(`payment.paymentEntries.${idx}.country`)}
                  className="col-span-full md:col-span-6"
                  required={
                    !isBusinessSkipValidation(
                      isBusiness,
                      recognition,
                      skipValidation
                    )
                  }
                />
                <Input
                  type="text"
                  label={t('form.labels.licensePlate')}
                  placeholder={t('form.placeholders.licensePlate')}
                  {...registerField(
                    `payment.paymentEntries.${idx}.licensePlate`
                  )}
                  error={getError(`payment.paymentEntries.${idx}.licensePlate`)}
                  className="col-span-full md:col-span-6"
                  required={!isBusiness}
                />
                <Box className="col-span-full flex justify-end">
                  <Button
                    intent="outlined"
                    onClick={() => onAdd(idx)}
                    disabled={disabled}
                  >
                    {t('action.save')}
                  </Button>
                </Box>
              </Grid>
            ) : null}
          </RecognitionCard>
        ))}
      </Box>
    </Box>
  );
};

export default Recognitions;
